/**
 * Top-level routing for the Teacher AI Hub web app.
 *
 * Routes:
 *   - `/login`               — mock-login form
 *   - `/teacher/dashboard`   — protected
 *   - `/teacher/courses`     — protected
 *   - `/teacher/courseware`  — protected
 *   - `/teacher/grading`     — protected
 *   - `/teacher/settings`    — protected
 *   - `/student/dashboard`   — protected
 *   - `/student/courses`     — protected
 *   - `/student/assistant`   — protected
 *   - `/student/tutor`       — protected
 *   - `/student/progress`    — protected
 *   - `/forbidden`           — 403 explainer (also rendered inline by RequireRole)
 *   - `*`                    — bounces unauthed traffic to /login, role-mismatched
 *                              traffic to 403, or to the persona's home for the
 *                              role currently signed in.
 *
 * Sprint 1 fills the teacher and student shells. `/admin/*` reuses the same
 * `<RequireRole>` once its shell lands.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { TeacherShell } from './components/TeacherShell';
import { StudentShell } from './components/StudentShell';
import { Login } from './routes/Login';
import { Forbidden } from './routes/Forbidden';
import { Dashboard } from './routes/teacher/Dashboard';
import { Courses } from './routes/teacher/Courses';
import { Courseware } from './routes/teacher/Courseware';
import { Grading } from './routes/teacher/Grading';
import { Settings } from './routes/teacher/Settings';
import { StudentDashboard } from './routes/student/Dashboard';
import { StudentCourses } from './routes/student/Courses';
import { StudentAssistant } from './routes/student/Assistant';
import { StudentTutor } from './routes/student/Tutor';
import { StudentProgress } from './routes/student/Progress';
import { RequireRole } from './auth/RouteGate';
import { useSession } from './auth/SessionProvider';

const TEACHER_ROLES = ['teacher'] as const;
const STUDENT_ROLES = ['student'] as const;

export function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forbidden" element={<Forbidden />} />

      <Route
        element={
          <RequireRole allowed={TEACHER_ROLES}>
            <TeacherShell />
          </RequireRole>
        }
      >
        <Route path="/teacher" element={<Navigate to="/teacher/dashboard" replace />} />
        <Route path="/teacher/dashboard" element={<Dashboard />} />
        <Route path="/teacher/courses" element={<Courses />} />
        <Route path="/teacher/courseware" element={<Courseware />} />
        <Route path="/teacher/grading" element={<Grading />} />
        <Route path="/teacher/settings" element={<Settings />} />
      </Route>

      <Route
        element={
          <RequireRole allowed={STUDENT_ROLES}>
            <StudentShell />
          </RequireRole>
        }
      >
        <Route path="/student" element={<Navigate to="/student/dashboard" replace />} />
        <Route path="/student/dashboard" element={<StudentDashboard />} />
        <Route path="/student/courses" element={<StudentCourses />} />
        <Route path="/student/assistant" element={<StudentAssistant />} />
        <Route path="/student/tutor" element={<StudentTutor />} />
        <Route path="/student/progress" element={<StudentProgress />} />
      </Route>

      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

function RootRedirect() {
  const { session } = useSession();
  if (!session) return <Navigate to="/login" replace />;
  if (session.principal.role === 'teacher') {
    return <Navigate to="/teacher/dashboard" replace />;
  }
  if (session.principal.role === 'student') {
    return <Navigate to="/student/dashboard" replace />;
  }
  return <Forbidden role={session.principal.role} />;
}
